/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, observable } from 'mobx';
import { tokenToCharIndices } from 'src/core/utils/labelingUtils';
import { LuisId } from 'src/luis/types/Resource';
import { EntityLabelStore, LabelRange } from 'src/luis/types/stores/utterances/EntityLabelStore';

/**
 * A label store factory function.
 */
export const createEntityLabelStore = (entityId: LuisId, labelIndices: LabelRange, roleId?: LuisId): EntityLabelStore => {
    const labelStore: EntityLabelStore = {
        entityId,
        roleId,
        ...labelIndices,

        /**
         * Sets the given role as role id of this label.
         * @param newRoleId
         */
        setRole(newRoleId) {
            this.roleId = newRoleId;
        },

        /**
         * Converts the character indices to token indices.
         *
         * @param tokenizedText The tokenized utterance text.
         */
        charToTokenIndices({ tokenizedText }) {
            let startLength: number = 0;
            let endLength: number = -2;
            let startTokenIndex: number;
            let endTokenIndex: number;

            for (let i: number = 0; i < tokenizedText.length; i += 1) {
                if (this.startCharIndex - startLength === 0) {
                    startTokenIndex = i;
                    break;
                } else {
                    startLength += tokenizedText[i].length + 1;
                }
            }

            for (let i: number = 0; i < tokenizedText.length; i += 1) {
                endLength += tokenizedText[i].length + 1;

                if (this.endCharIndex - endLength === 0) {
                    endTokenIndex = i;
                    break;
                }
            }

            return { startTokenIndex, endTokenIndex };
        },

        /**
         * Returns current entity character indices converted from token indices. Requires original utterance
         * text and tokenized text for calculations.
         *
         * @param text The original utterance text the entity was labeled in.
         * @param tokenizedText The original utterance tokenized text.
         */
        tokenToCharIndices({ text, tokenizedText }) {
            return tokenToCharIndices({
                text,
                tokenizedText,
                startTokenIndex: this.startTokenIndex,
                endTokenIndex: this.endTokenIndex
            });
        }
    };

    return observable(labelStore, { roleId: observable, setRole: action }, { deep: false });
};
